<template>
    <div class="form-edit-page">
        <div class="page-header">
            <h1>Cadastro de Unidades de Inspeção (UI)</h1>
            <p>
                Cadastro de unidades vinculadas ao estabelecimento e à respectiva área de inspeção.
            </p>
        </div>

        <div class="form-card">
            <h2>Nova Unidade de Inspeção</h2>

            <div class="row">
                <div class="field">
                    <label>Estabelecimento</label>
                    <select v-model="form.idEstabelecimento" @change="onChangeEstabelecimento">
                        <option :value="null">Selecione</option>
                        <option
                            v-for="est in lsEstabelecimentos"
                            :key="est.id"
                            :value="est.id"
                        >
                            {{ est.nome }}
                        </option>
                    </select>
                </div>

                <div class="field">
                    <label>Área de Inspeção (AI)</label>
                    <select v-model="form.idAreaInspecao">
                        <option :value="null">Selecione</option>
                        <option
                            v-for="area in lsAreasFiltradas"
                            :key="area.id"
                            :value="area.id"
                        >
                            {{ area.codigo }} - {{ area.nome }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="row">
                <div class="field field-small">
                    <label>Código</label>
                    <input
                        v-model="form.codigo"
                        type="text"
                        maxlength="10"
                        placeholder="Ex: UI-01"
                    />
                </div>

                <div class="field">
                    <label>Nome da Unidade</label>
                    <input
                        v-model="form.nome"
                        type="text"
                        maxlength="80"
                        placeholder="Ex: Linha de Abate 01"
                    />
                </div>
            </div>

            <div class="row">
                <div class="field">
                    <label>Descrição</label>
                    <input
                        v-model="form.descricao"
                        type="text"
                        maxlength="150"
                        placeholder="Descreva resumidamente a unidade de inspeção"
                    />
                </div>
            </div>

            <div class="row">
                <div class="field">
                    <label>Situação</label>
                    <select v-model="form.situacao">
                        <option value="ATIVO">Ativo</option>
                        <option value="INATIVO">Inativo</option>
                    </select>
                </div>

                <div class="field field-small">
                    <label>Ordem</label>
                    <input
                        v-model.number="form.ordem"
                        type="number"
                        min="1"
                        placeholder="0"
                    />
                </div>
            </div>

            <div class="row">
                <div class="field">
                    <label>Observações</label>
                    <textarea
                        v-model="form.observacoes"
                        rows="3"
                        maxlength="300"
                        placeholder="Informações complementares"
                    ></textarea>
                </div>
            </div>

            <div class="form-actions">
                <button class="btn btn-primary" @click="adicionarItem">Adicionar à lista</button>
                <button class="btn btn-secondary" @click="limparForm">Limpar</button>
            </div>
        </div>

        <div class="table-card">
            <div class="table-header">
                <h2>Unidades cadastradas</h2>
                <span>{{ lsUI.length }} registro(s)</span>
            </div>

            <div class="table-filters">
                <div class="field">
                    <label>Filtrar por estabelecimento</label>
                    <select v-model="filtro.idEstabelecimento">
                        <option :value="null">Todos</option>
                        <option
                            v-for="est in lsEstabelecimentos"
                            :key="est.id"
                            :value="est.id"
                        >
                            {{ est.nome }}
                        </option>
                    </select>
                </div>

                <div class="field">
                    <label>Filtrar por situação</label>
                    <select v-model="filtro.situacao">
                        <option value="">Todas</option>
                        <option value="ATIVO">Ativo</option>
                        <option value="INATIVO">Inativo</option>
                    </select>
                </div>
            </div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Unidade</th>
                            <th>Estabelecimento</th>
                            <th>Área de Inspeção</th>
                            <th>Situação</th>
                            <th>Ordem</th>
                            <th style="width: 120px;">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="lsUIFiltrada.length === 0">
                            <td colspan="7" class="empty-table">
                                Nenhuma unidade de inspeção cadastrada.
                            </td>
                        </tr>

                        <tr
                            v-for="item in lsUIFiltrada"
                            :key="item.id"
                        >
                            <td>{{ item.codigo }}</td>
                            <td>
                                <div class="cell-main">{{ item.nome }}</div>
                                <div class="cell-sub" v-if="item.descricao">
                                    {{ item.descricao }}
                                </div>
                            </td>
                            <td>{{ item.nmEstabelecimento }}</td>
                            <td>{{ item.nmAreaInspecao }}</td>
                            <td>
                                <span
                                    class="status-badge"
                                    :class="item.situacao === 'ATIVO' ? 'ativo' : 'inativo'"
                                >
                                    {{ item.situacao }}
                                </span>
                            </td>
                            <td>{{ item.ordem }}</td>
                            <td>
                                <button
                                    class="btn-action btn-delete"
                                    @click="excluirItem(item.id)"
                                >
                                    Excluir
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';

const lsEstabelecimentos = ref([
    { id: 1, nome: 'Frigorífico Bom Corte' },
    { id: 2, nome: 'Laticínios Serra Azul' },
    { id: 3, nome: 'Entreposto de Mel Doce Campo' }
]);

const lsAreasInspecao = ref([
    { id: 1, idEstabelecimento: 1, codigo: 'AI-01', nome: 'Recepção de Animais' },
    { id: 2, idEstabelecimento: 1, codigo: 'AI-02', nome: 'Abate' },
    { id: 3, idEstabelecimento: 1, codigo: 'AI-03', nome: 'Câmara Fria' },

    { id: 4, idEstabelecimento: 2, codigo: 'AI-01', nome: 'Recepção de Leite' },
    { id: 5, idEstabelecimento: 2, codigo: 'AI-02', nome: 'Pasteurização' },
    { id: 6, idEstabelecimento: 2, codigo: 'AI-03', nome: 'Envase' },

    { id: 7, idEstabelecimento: 3, codigo: 'AI-01', nome: 'Recepção do Mel' },
    { id: 8, idEstabelecimento: 3, codigo: 'AI-02', nome: 'Filtragem' },
    { id: 9, idEstabelecimento: 3, codigo: 'AI-03', nome: 'Envase e Rotulagem' }
]);

const lsUI = ref([
    {
        id: 1,
        idEstabelecimento: 1,
        nmEstabelecimento: 'Frigorífico Bom Corte',
        idAreaInspecao: 2,
        nmAreaInspecao: 'Abate',
        codigo: 'UI-01',
        nome: 'Linha de Abate 01',
        descricao: 'Área principal de inspeção da linha de abate',
        situacao: 'ATIVO',
        ordem: 1,
        observacoes: ''
    },
    {
        id: 2,
        idEstabelecimento: 2,
        nmEstabelecimento: 'Laticínios Serra Azul',
        idAreaInspecao: 6,
        nmAreaInspecao: 'Envase',
        codigo: 'UI-02',
        nome: 'Sala de Envase 01',
        descricao: 'Unidade destinada ao envase final dos produtos',
        situacao: 'ATIVO',
        ordem: 2,
        observacoes: ''
    }
]);

const form = reactive({
    idEstabelecimento: null,
    idAreaInspecao: null,
    codigo: '',
    nome: '',
    descricao: '',
    situacao: 'ATIVO',
    ordem: 1,
    observacoes: ''
});

const filtro = reactive({
    idEstabelecimento: null,
    situacao: ''
});

const lsAreasFiltradas = computed(() => {
    if (!form.idEstabelecimento) return [];
    return lsAreasInspecao.value.filter(
        a => a.idEstabelecimento === form.idEstabelecimento
    );
});

const lsUIFiltrada = computed(() => {
    return lsUI.value.filter(item => {
        const filtroEstab =
            !filtro.idEstabelecimento || item.idEstabelecimento === filtro.idEstabelecimento;

        const filtroSit =
            !filtro.situacao || item.situacao === filtro.situacao;

        return filtroEstab && filtroSit;
    });
});

function onChangeEstabelecimento() {
    form.idAreaInspecao = null;
}

function limparForm() {
    form.idEstabelecimento = null;
    form.idAreaInspecao = null;
    form.codigo = '';
    form.nome = '';
    form.descricao = '';
    form.situacao = 'ATIVO';
    form.ordem = 1;
    form.observacoes = '';
}

function validarForm() {
    if (!form.idEstabelecimento) {
        throw new Error('Informe o estabelecimento.');
    }

    if (!form.idAreaInspecao) {
        throw new Error('Informe a área de inspeção.');
    }

    if (!form.codigo.trim()) {
        throw new Error('Informe o código da unidade de inspeção.');
    }

    if (!form.nome.trim()) {
        throw new Error('Informe o nome da unidade de inspeção.');
    }

    const jaExiste = lsUI.value.some(item =>
        item.idEstabelecimento === form.idEstabelecimento &&
        item.idAreaInspecao === form.idAreaInspecao &&
        item.codigo.trim().toUpperCase() === form.codigo.trim().toUpperCase()
    );

    if (jaExiste) {
        throw new Error('Já existe uma UI com este código para a área informada.');
    }
}

function adicionarItem() {
    try {
        validarForm();

        const estabelecimento = lsEstabelecimentos.value.find(
            e => e.id === form.idEstabelecimento
        );

        const area = lsAreasInspecao.value.find(
            a => a.id === form.idAreaInspecao
        );

        lsUI.value.push({
            id: Date.now(),
            idEstabelecimento: form.idEstabelecimento,
            nmEstabelecimento: estabelecimento?.nome ?? '',
            idAreaInspecao: form.idAreaInspecao,
            nmAreaInspecao: area?.nome ?? '',
            codigo: form.codigo.trim().toUpperCase(),
            nome: form.nome.trim(),
            descricao: form.descricao.trim(),
            situacao: form.situacao,
            ordem: form.ordem || 0,
            observacoes: form.observacoes.trim()
        });

        limparForm();
    } catch (error) {
        alert(error.message || 'Erro ao adicionar registro.');
    }
}

function excluirItem(id) {
    const idx = lsUI.value.findIndex(item => item.id === id);
    if (idx >= 0) {
        lsUI.value.splice(idx, 1);
    }
}
</script>

<style scoped>
.form-edit-page {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #f5f7fb;
}

.page-header h1 {
    margin: 0 0 6px;
    font-size: 24px;
    color: #1f2937;
}

.page-header p {
    margin: 0;
    color: #6b7280;
}

.form-card,
.table-card {
    background: #ffffff;
    border: 1px solid #dbe3ee;
    border-radius: 10px;
    padding: 20px;
}

.form-card h2,
.table-card h2 {
    margin: 0 0 16px;
    font-size: 18px;
    color: #1f2937;
}

.row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.field {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 220px;
}

.field-small {
    max-width: 180px;
}

label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}

input,
select,
textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #cfd8e3;
    border-radius: 8px;
    font-size: 14px;
    background: #fff;
    box-sizing: border-box;
}

textarea {
    resize: vertical;
}

.form-actions {
    display: flex;
    gap: 10px;
    margin-top: 10px;
}

.btn {
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
}

.btn-primary {
    background: #2563eb;
    color: #fff;
}

.btn-secondary {
    background: #e5e7eb;
    color: #111827;
}

.table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 12px;
}

.table-header span {
    font-size: 13px;
    color: #6b7280;
}

.table-filters {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
}

.data-table th,
.data-table td {
    border-bottom: 1px solid #e5e7eb;
    padding: 12px 10px;
    text-align: left;
    vertical-align: top;
    font-size: 14px;
}

.data-table th {
    background: #f9fafb;
    color: #374151;
    font-weight: 600;
}

.empty-table {
    text-align: center;
    color: #6b7280;
    padding: 24px !important;
}

.cell-main {
    font-weight: 600;
    color: #111827;
}

.cell-sub {
    margin-top: 4px;
    font-size: 12px;
    color: #6b7280;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.status-badge.ativo {
    background: #dcfce7;
    color: #166534;
}

.status-badge.inativo {
    background: #fee2e2;
    color: #991b1b;
}

.btn-action {
    border: none;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    cursor: pointer;
}

.btn-delete {
    background: #fee2e2;
    color: #991b1b;
}
</style>